/**
 * `BiChartGallery` (D529): every BI chart type with fixture data, plus the loading and empty states and the export menu —
 * the page the theme-proof harness mounts to measure and capture them. The numbers are plain; a real screen injects its
 * market's formatters. The coordinates are an arbitrary patch of the globe, not a place.
 */
import { useState, type ReactElement, type ReactNode } from 'react';
import { binGeoPoints } from '@vello/charts-core';
import { BarList } from './bars';
import { ColumnChart } from './column-chart';
import { LineChart } from './line-chart';
import { BulletGauge } from './gauge';
import { Funnel, Waterfall } from './flow-charts';
import { CalendarHeatmap, CohortGrid, HeatmapGrid } from './heatmap';
import { ComboChart, Histogram, ParetoChart, StackedAreaChart, StackedBarChart } from './axis-charts';
import { DataTable } from './data-table';
import { GeoMap, geoCellsFromBinning } from './geo-map';
import type { ChartExportLabels } from './export';
import type { ChartSelection } from './bi-kit';

const EXPORT: ChartExportLabels = { menu: 'Export', png: 'Image (PNG)', csv: 'Data (CSV)' };
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const WEEKDAYS_SUN_FIRST = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const HOURS = Array.from({ length: 12 }, (_, i) => `${String(i * 2).padStart(2, '0')}h`);

/** A small deterministic pseudo-random sequence (no Math.random: the gallery is the same on every render). */
function sequence(seed: number, count: number): number[] {
  let s = seed;
  return Array.from({ length: count }, () => {
    s = (s * 1664525 + 1013904223) % 4294967296;
    return s / 4294967296;
  });
}

const HEAT_VALUES = WEEKDAYS.map((_, d) => sequence(7 + d, HOURS.length).map((r, h) => (h < 3 ? null : Math.round(r * 40 + (h > 7 && h < 10 ? 60 : 0) + (d > 4 ? 15 : 0)))));
const CALENDAR_DAYS = (() => {
  const base = Date.UTC(2026, 6, 6); // a Monday
  const r = sequence(99, 100);
  return Array.from({ length: 100 }, (_, i) => {
    const d = new Date(base + i * 86_400_000);
    return { day: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`, value: i % 17 === 5 ? null : Math.round((r[i] ?? 0) * 80) };
  });
})();
const GEO_POINTS = (() => {
  const r = sequence(5, 240);
  return Array.from({ length: 120 }, (_, i) => ({
    lat: 10 + (r[i * 2] ?? 0) * 0.06 + (i % 5 === 0 ? 0.02 : 0),
    lng: 20 + (r[i * 2 + 1] ?? 0) * 0.08 - (i % 7 === 0 ? 0.03 : 0),
  }));
})();
const GEO_CELLS = geoCellsFromBinning(binGeoPoints(GEO_POINTS, { cellMeters: 900 }), (c) => `Cell ${c.key}`);
const DAILY = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const RIDES = [420, 510, 480, 560, 690, 810, 640];
const CANCEL_RATE = [0.12, 0.1, 0.11, 0.09, 0.14, 0.18, 0.13];
const WAITS = [3.1, 4.2, 2.8, 5.5, 4.9, 3.7, 6.1, 4.4, 3.3, 7.8, 5.2, 4.1, 3.9, 9.5, 4.6, 5.0, 3.5, 2.9, 6.6, 4.0, 4.8, 5.7, 3.2, 11.2, 4.3];

const COHORTS = [
  { cohort: 'Wk 1', size: 320, retained: [320, 160, 112, 90, 74, 66] },
  { cohort: 'Wk 2', size: 280, retained: [280, 140, 98, 76, 63, null] },
  { cohort: 'Wk 3', size: 360, retained: [360, 198, 130, 101, null, null] },
  { cohort: 'Wk 4', size: 300, retained: [300, 150, 105, null, null, null] },
  { cohort: 'Wk 5', size: 340, retained: [340, 187, null, null, null, null] },
  { cohort: 'Wk 6', size: 310, retained: [310, null, null, null, null, null] },
];

export interface BiChartGalleryProps {
  /** `false` renders every chart in its final state with no motion (screenshots, tests). */
  readonly animate?: boolean;
}

function Cell({ id, title, wide = false, children }: { id: string; title: string; wide?: boolean; children: ReactNode }): ReactElement {
  return (
    <section data-gallery={id} className={wide ? 'vello-chart-gallery__cell vello-chart-gallery__cell--wide' : 'vello-chart-gallery__cell'}>
      <h3 className="vello-chart-gallery__title">{title}</h3>
      {children}
    </section>
  );
}

/** Every BI chart type with fixture data, the loading and empty states, and a selection read-out. */
export function BiChartGallery({ animate }: BiChartGalleryProps): ReactElement {
  const motion = animate !== undefined ? { animate } : {};
  const [picked, setPicked] = useState<ChartSelection | null>(null);
  const onSelect = (s: ChartSelection): void => setPicked(s);
  /** The values chosen on one dimension: a chart only shows the selection of the dimension it plots, like a real cross-filter. */
  const sel = (...dimensions: string[]): string[] => (picked !== null && dimensions.includes(picked.dimension) ? [picked.value] : []);

  return (
    <div className="vello-chart-gallery vello-chart-gallery--bi">
      <Cell id="select" title="Selection (the cross-filter contract)" wide>
        <p style={{ margin: 0 }} aria-live="polite">
          {picked === null ? 'Click or press Enter on a mark in any chart below.' : `onSelect({ dimension: "${picked.dimension}", value: "${picked.value}" })`}
        </p>
      </Cell>
      <Cell id="heatmap" title="Heatmap grid: hour × weekday" wide>
        <HeatmapGrid title="Rides by hour and weekday" rowLabels={WEEKDAYS} colLabels={HOURS} values={HEAT_VALUES} rowDimension="weekday" colDimension="hour" summaryWords={{ highest: 'Busiest', lowest: 'Quietest' }} exportLabels={EXPORT} onSelect={onSelect} selected={sel('weekday', 'hour')} {...motion} />
      </Cell>
      <Cell id="calendar" title="Heatmap: calendar" wide>
        <CalendarHeatmap title="Rides per day" days={CALENDAR_DAYS} weekStart={1} weekdayLabels={WEEKDAYS_SUN_FIRST} monthLabels={MONTHS} dimension="day" exportLabels={EXPORT} onSelect={onSelect} selected={sel('day')} {...motion} />
      </Cell>
      <Cell id="geo" title="Geo map: grid cells (static fallback, no tiles)">
        <GeoMap title="Demand by cell" cells={GEO_CELLS} valueLabel="Requests" height={280} dimension="cell" exportLabels={EXPORT} onSelect={onSelect} selected={sel('cell')} {...motion} />
      </Cell>
      <Cell id="geo-points" title="Geo map: bubbles and a flow">
        <GeoMap
          title="Hubs and a corridor"
          points={[
            { key: 'h1', label: 'Hub A', lat: 10.02, lng: 20.02, value: 120 },
            { key: 'h2', label: 'Hub B', lat: 10.05, lng: 20.07, value: 60 },
            { key: 'h3', label: 'Hub C', lat: 10.01, lng: 20.06, value: 25 },
          ]}
          flows={[{ key: 'f1', label: 'Hub A to Hub B', from: { lat: 10.02, lng: 20.02 }, to: { lat: 10.05, lng: 20.07 }, value: 40 }]}
          height={280}
          dimension="hub"
          onSelect={onSelect}
          selected={sel('hub')}
          {...motion}
        />
      </Cell>
      <Cell id="bullet" title="Bullet gauge with target and bands">
        <div className="vello-chart-gallery__stack">
          <BulletGauge label="On-time pickups" value={72} target={80} bands={[60, 85, 100]} bandLabels={['Poor', 'Fair', 'Good']} reachedLabel="Target met" missedLabel="Below target" targetLabel="Target" formatValue={(v) => `${v}%`} exportLabels={EXPORT} {...motion} />
          <BulletGauge label="Average wait (min)" value={4.2} target={5} bands={[3, 6, 10]} lowerIsBetter reachedLabel="Target met" missedLabel="Above target" targetLabel="Target" {...motion} />
        </div>
      </Cell>
      <Cell id="target" title="Target line and band on line and columns">
        <div className="vello-chart-gallery__stack">
          <LineChart title="Rides" labels={DAILY} series={[{ key: 'r', label: 'Rides', values: RIDES }]} target={{ value: 600, label: 'Goal', band: [550, 650] }} height={190} exportLabels={EXPORT} dimension="weekday" onSelect={onSelect} selected={sel('weekday')} {...motion} />
          <ColumnChart title="Rides" categories={DAILY} series={[{ key: 'r', label: 'Rides', values: RIDES }]} target={{ value: 600, label: 'Goal' }} height={190} dimension="weekday" onSelect={onSelect} selected={sel('weekday')} {...motion} />
        </div>
      </Cell>
      <Cell id="histogram" title="Histogram with cumulative curve and markers">
        <Histogram title="Wait time (min)" values={WAITS} binOptions={{ bins: 6 }} cumulative countLabel="Rides" cumulativeLabel="Cumulative share" markers={[{ value: 4.6, label: 'Median' }]} dimension="wait" exportLabels={EXPORT} onSelect={onSelect} selected={sel('wait')} {...motion} />
      </Cell>
      <Cell id="funnel" title="Funnel">
        <Funnel
          title="Booking funnel"
          steps={[
            { key: 'open', label: 'Opened the app', value: 12400 },
            { key: 'quote', label: 'Requested a quote', value: 6100 },
            { key: 'confirm', label: 'Confirmed the ride', value: 3300 },
            { key: 'done', label: 'Ride completed', value: 2900 },
          ]}
          words={{ fromPrevious: 'of the previous step', fromFirst: 'of the first step', dropOff: 'Lost', overall: 'Overall conversion' }}
          dimension="step"
          exportLabels={EXPORT}
          onSelect={onSelect}
          selected={sel('step')}
          {...motion}
        />
      </Cell>
      <Cell id="cohort" title="Cohort retention" wide>
        <CohortGrid title="Weekly retention" cohorts={COHORTS} periodLabel={(p) => `W${p}`} sizeLabel="Size" averageLabel="Average" pendingLabel="Not observed yet" exportLabels={EXPORT} onSelect={onSelect} selected={sel('cohort')} {...motion} />
      </Cell>
      <Cell id="waterfall" title="Waterfall: gross to net">
        <Waterfall
          title="From gross to net"
          steps={[
            { key: 'gross', label: 'Gross', value: 10000, kind: 'total' },
            { key: 'promo', label: 'Promotions', value: -900 },
            { key: 'fee', label: 'Fees', value: -1300 },
            { key: 'tip', label: 'Tips', value: 450 },
            { key: 'refund', label: 'Refunds', value: -250 },
            { key: 'net', label: 'Net', kind: 'total' },
          ]}
          dimension="line"
          exportLabels={EXPORT}
          onSelect={onSelect}
          selected={sel('line')}
          {...motion}
        />
      </Cell>
      <Cell id="combo" title="Combo: bars + line on two axes">
        <ComboChart
          title="Rides and cancel rate"
          categories={DAILY}
          bars={[{ key: 'rides', label: 'Rides', values: RIDES }]}
          lines={[{ key: 'cancel', label: 'Cancel rate', values: CANCEL_RATE }]}
          leftAxisLabel="Rides"
          rightAxisLabel="Cancel rate"
          formatRight={(v) => `${Math.round(v * 100)}%`}
          dimension="weekday"
          exportLabels={EXPORT}
          onSelect={onSelect}
          selected={sel('weekday')}
          {...motion}
        />
      </Cell>
      <Cell id="stacked-area" title="Stacked area and 100 % area">
        <div className="vello-chart-gallery__stack">
          <StackedAreaChart title="Rides by payment" labels={DAILY} series={[{ key: 'cash', label: 'Cash', values: [200, 220, 210, 230, 260, 300, 250] }, { key: 'card', label: 'Card', values: [180, 240, 220, 270, 330, 400, 310] }, { key: 'wallet', label: 'Wallet', values: [40, 50, 50, 60, 100, 110, 80] }]} totalLabel="Total" height={190} exportLabels={EXPORT} {...motion} />
          <StackedAreaChart title="Mix by payment" labels={DAILY} percent series={[{ key: 'cash', label: 'Cash', values: [200, 220, 210, 230, 260, 300, 250] }, { key: 'card', label: 'Card', values: [180, 240, 220, 270, 330, 400, 310] }, { key: 'wallet', label: 'Wallet', values: [40, 50, 50, 60, 100, 110, 80] }]} height={190} {...motion} />
        </div>
      </Cell>
      <Cell id="stacked-bars" title="Stacked horizontal bars, absolute and 100 %">
        <div className="vello-chart-gallery__stack">
          <StackedBarChart title="Orders by status" categories={['North', 'Centre', 'South', 'East']} series={[{ key: 'done', label: 'Delivered', values: [320, 410, 280, 190] }, { key: 'late', label: 'Late', values: [40, 62, 31, 22] }, { key: 'lost', label: 'Cancelled', values: [18, 25, 12, 9] }]} totalLabel="Total" dimension="zone" exportLabels={EXPORT} onSelect={onSelect} selected={sel('zone')} {...motion} />
          <StackedBarChart title="Share by status" percent categories={['North', 'Centre', 'South', 'East']} series={[{ key: 'done', label: 'Delivered', values: [320, 410, 280, 190] }, { key: 'late', label: 'Late', values: [40, 62, 31, 22] }, { key: 'lost', label: 'Cancelled', values: [18, 25, 12, 9] }]} {...motion} />
        </div>
      </Cell>
      <Cell id="column-percent" title="100 % stacked columns">
        <ColumnChart title="Mix per weekday" categories={DAILY} percent series={[{ key: 'a', label: 'Economy', values: [60, 70, 65, 80, 90, 100, 85] }, { key: 'b', label: 'Comfort', values: [30, 35, 30, 40, 55, 70, 50] }]} height={200} {...motion} />
      </Cell>
      <Cell id="pareto" title="Pareto / ABC">
        <ParetoChart
          title="Revenue by item"
          items={[
            { key: 'burger', label: 'Burger', value: 4200 },
            { key: 'pizza', label: 'Pizza', value: 3100 },
            { key: 'salad', label: 'Salad', value: 1200 },
            { key: 'soup', label: 'Soup', value: 800 },
            { key: 'wrap', label: 'Wrap', value: 450 },
            { key: 'cake', label: 'Cake', value: 300 },
            { key: 'tea', label: 'Tea', value: 150 },
          ]}
          classLabel={(c) => (c === 'A' ? 'Top 80 %' : c === 'B' ? 'Next 15 %' : 'Tail')}
          cumulativeLabel="Cumulative share"
          dimension="item"
          exportLabels={EXPORT}
          onSelect={onSelect}
          selected={sel('item')}
          {...motion}
        />
      </Cell>
      <Cell id="table" title="Table: conditional formatting, in-cell bar and sparkline" wide>
        <DataTable
          title="Cities"
          columns={[
            { key: 'city', header: 'City' },
            { key: 'rides', header: 'Rides', kind: 'bar' },
            { key: 'cancel', header: 'Cancel rate', kind: 'heat', format: (v) => `${v}%`, rules: [{ when: 'above', value: 15, tone: 'danger', label: 'Above the limit' }, { when: 'between', value: 10, to: 15, tone: 'warning', label: 'Near the limit' }] },
            { key: 'trend', header: 'Last 7 days', kind: 'spark' },
          ]}
          rows={[
            { key: 'n', values: { city: 'North', rides: 4200, cancel: 8, trend: [10, 12, 9, 14, 15, 17, 16] } },
            { key: 'c', values: { city: 'Centre', rides: 6100, cancel: 12, trend: [20, 22, 21, 19, 24, 26, 25] } },
            { key: 's', values: { city: 'South', rides: 2600, cancel: 18, trend: [9, 8, 7, 7, 6, 8, 5] } },
            { key: 'e', values: { city: 'East', rides: 1900, cancel: 6, trend: [4, 5, 5, 6, 8, 7, 9] } },
          ]}
          selectColumn="city"
          dimension="city"
          exportLabels={EXPORT}
          onSelect={onSelect}
          selected={sel('city')}
        />
      </Cell>
      <Cell id="states" title="Loading and empty states" wide>
        <div className="vello-chart-gallery__stats">
          <HeatmapGrid rowLabels={WEEKDAYS} colLabels={HOURS} values={[]} loading loadingLabel="Loading" />
          <Funnel steps={[]} words={{ fromPrevious: 'of previous' }} loading loadingLabel="Loading" />
          <GeoMap loading loadingLabel="Loading" height={120} />
          <Histogram values={[]} emptyLabel="No data for this period" height={120} />
          <Waterfall steps={[]} emptyLabel="No data for this period" height={120} />
          <ParetoChart items={[]} emptyLabel="No data for this period" height={120} />
          <BarList items={[]} emptyLabel="No data for this period" />
        </div>
      </Cell>
    </div>
  );
}
